import { useEffect, useMemo, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { format, formatDistanceToNowStrict } from "date-fns";
import { Mail, Phone, RefreshCw, Search, Trash2 } from "lucide-react";
import { Link } from "react-router-dom";
import { CopyableEmail } from "@/components/common/CopyableEmail";
import PageHeader from "@/components/common/PageHeader";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { useDebouncedValue } from "@/hooks/ui/useDebouncedValue";
import type { InactiveGenie } from "@/types/admin/inactive-genies";
import { GENIE_TYPES, type GenieType, getGenieTypeMeta } from "@/types/genie-type";
import { DeleteInactiveGeniesSheet } from "./DeleteInactiveGeniesSheet";
import { INACTIVE_GENIES_PAGE_SIZE, INACTIVE_GENIES_QUERY_KEY, useInactiveGenies } from "./hooks/useInactiveGenies";

const SEARCH_DEBOUNCE_MS = 350;
const COLUMN_COUNT = 8;
const PERIOD_OPTIONS = [
  { days: 30, label: "More than 30 days" },
  { days: 90, label: "More than 90 days" },
  { days: 180, label: "More than 6 months" },
  { days: 365, label: "More than a year" },
];

const formatDate = (iso: string) => format(new Date(iso), "d MMM yyyy");

/** Someone saved the genie's settings after its last conversation (or creation): it may still be in use. */
const isEditedSinceActivity = (genie: InactiveGenie) =>
  !!genie.updated_at && new Date(genie.updated_at) > new Date(genie.last_activity_at);

/**
 * Genies nobody has talked to for a while (no conversation since the chosen
 * cutoff; never-used genies count from creation), least recently active first,
 * so an admin can pick the ones to clean up and delete them in bulk.
 */
const InactiveGeniesPage = () => {
  const queryClient = useQueryClient();
  const [page, setPage] = useState(1);
  const [days, setDays] = useState(90);
  const [typeFilter, setTypeFilter] = useState<GenieType | "all">("all");
  const [search, setSearch] = useState("");
  const [includeDemo, setIncludeDemo] = useState(false);
  const [includeMarketplace, setIncludeMarketplace] = useState(false);
  const [selected, setSelected] = useState<Map<string, InactiveGenie>>(new Map());
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);
  const debouncedSearch = useDebouncedValue(search.trim(), SEARCH_DEBOUNCE_MS);

  const { data, isLoading, isFetching, refetch } = useInactiveGenies(
    {
      days,
      type: typeFilter === "all" ? undefined : typeFilter,
      search: debouncedSearch || undefined,
      includeDemo: includeDemo || undefined,
      includeMarketplace: includeMarketplace || undefined,
    },
    page,
  );

  useEffect(() => {
    setPage(1);
    setSelected(new Map());
  }, [days, typeFilter, debouncedSearch, includeDemo, includeMarketplace]);

  const genies = data?.genies ?? [];
  const totalCount = data?.count ?? 0;
  const selectedGenies = useMemo(() => [...selected.values()], [selected]);
  const allOnPageSelected = genies.length > 0 && genies.every((g) => selected.has(g.id));
  const someOnPageSelected = genies.some((g) => selected.has(g.id));

  const toggleGenie = (genie: InactiveGenie) =>
    setSelected((prev) => {
      const next = new Map(prev);
      if (next.has(genie.id)) next.delete(genie.id);
      else next.set(genie.id, genie);
      return next;
    });

  const togglePage = () =>
    setSelected((prev) => {
      const next = new Map(prev);
      if (allOnPageSelected) genies.forEach((g) => next.delete(g.id));
      else genies.forEach((g) => next.set(g.id, g));
      return next;
    });

  const handleDeleted = (deletedIds: string[]) => {
    setSelected((prev) => {
      const next = new Map(prev);
      deletedIds.forEach((id) => next.delete(id));
      return next;
    });
    queryClient.invalidateQueries({ queryKey: [INACTIVE_GENIES_QUERY_KEY] });
  };

  return (
    <div className="min-h-screen bg-genie-background p-4 md:p-8">
      <div className="max-w-[1920px] mx-auto">
        <PageHeader
          title="Inactive Genies"
          genieIcon="genie-lamp"
          subtitle="Genies nobody has talked to in a while, oldest activity first"
          action={
            <Button onClick={() => refetch()} variant="outline" className="gap-2 min-h-[44px]" disabled={isFetching}>
              <RefreshCw className={`h-4 w-4 ${isFetching ? "animate-spin" : ""}`} />
              Refresh
            </Button>
          }
        />

        <div className="bg-white rounded-lg p-4 mb-6 border border-genie-border">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="relative md:col-span-2">
              <Search className="absolute left-3 top-3 h-4 w-4 text-genie-text-muted" />
              <Input
                placeholder="Search by genie name, or owner name or email..."
                className="pl-10 text-base sm:text-sm"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>
            <Select value={String(days)} onValueChange={(value) => setDays(Number(value))}>
              <SelectTrigger className="text-base sm:text-sm">
                <SelectValue placeholder="Inactive for" />
              </SelectTrigger>
              <SelectContent>
                {PERIOD_OPTIONS.map((option) => (
                  <SelectItem key={option.days} value={String(option.days)}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={typeFilter} onValueChange={(value) => setTypeFilter(value as GenieType | "all")}>
              <SelectTrigger className="text-base sm:text-sm">
                <SelectValue placeholder="Type" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All types</SelectItem>
                {GENIE_TYPES.map((type) => (
                  <SelectItem key={type.id} value={type.id}>
                    {type.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="flex flex-wrap gap-x-6 mt-3">
            <label className="flex items-center gap-2 min-h-[44px] cursor-pointer text-sm text-genie-text-secondary">
              <Checkbox checked={includeDemo} onCheckedChange={(checked) => setIncludeDemo(checked === true)} />
              Show demo genies
            </label>
            <label className="flex items-center gap-2 min-h-[44px] cursor-pointer text-sm text-genie-text-secondary">
              <Checkbox
                checked={includeMarketplace}
                onCheckedChange={(checked) => setIncludeMarketplace(checked === true)}
              />
              Show marketplace genies
            </label>
          </div>
        </div>

        {selected.size > 0 && (
          <div className="sticky top-2 z-10 mb-4 flex flex-col sm:flex-row sm:items-center justify-between gap-3 rounded-lg border border-genie-primary/30 bg-white p-3 shadow-sm">
            <span className="text-sm text-genie-text-primary">
              <span className="font-semibold">{selected.size}</span> {selected.size === 1 ? "genie" : "genies"} selected
            </span>
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => setSelected(new Map())} className="min-h-[44px]">
                Clear
              </Button>
              <Button variant="destructive" onClick={() => setIsDeleteOpen(true)} className="gap-2 min-h-[44px]">
                <Trash2 className="h-4 w-4" />
                Delete selected
              </Button>
            </div>
          </div>
        )}

        <div className="bg-white rounded-lg border border-genie-border overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-12">
                  <Checkbox
                    checked={allOnPageSelected ? true : someOnPageSelected ? "indeterminate" : false}
                    onCheckedChange={togglePage}
                    disabled={genies.length === 0}
                    aria-label="Select all on this page"
                  />
                </TableHead>
                <TableHead>Genie</TableHead>
                <TableHead className="hidden md:table-cell">Owner</TableHead>
                <TableHead>Last conversation</TableHead>
                <TableHead className="hidden md:table-cell">Last updated</TableHead>
                <TableHead className="hidden lg:table-cell">Conversations</TableHead>
                <TableHead className="hidden lg:table-cell">Created</TableHead>
                <TableHead className="hidden sm:table-cell">Attached</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {isLoading ? (
                <TableRow>
                  <TableCell colSpan={COLUMN_COUNT} className="text-center py-8 text-genie-text-secondary">
                    <RefreshCw className="h-6 w-6 animate-spin mx-auto mb-2 text-genie-primary" />
                    Looking for quiet genies...
                  </TableCell>
                </TableRow>
              ) : genies.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={COLUMN_COUNT} className="text-center py-8 text-genie-text-muted">
                    Every genie here has been used recently
                  </TableCell>
                </TableRow>
              ) : (
                genies.map((genie) => (
                  <TableRow
                    key={genie.id}
                    onClick={() => toggleGenie(genie)}
                    data-state={selected.has(genie.id) ? "selected" : undefined}
                    className="cursor-pointer hover:bg-genie-primary/5"
                  >
                    <TableCell onClick={(e) => e.stopPropagation()}>
                      <Checkbox
                        checked={selected.has(genie.id)}
                        onCheckedChange={() => toggleGenie(genie)}
                        aria-label={`Select ${genie.name}`}
                      />
                    </TableCell>
                    <TableCell>
                      <Link
                        to={`/genie-hub/my-genies/${genie.id}`}
                        onClick={(e) => e.stopPropagation()}
                        className="text-sm font-medium text-genie-text-primary hover:text-genie-primary hover:underline"
                      >
                        {genie.name}
                      </Link>
                      <div className="flex items-center gap-1.5 text-xs text-genie-text-secondary">
                        {getGenieTypeMeta(genie.type).label}
                        {genie.is_demo && (
                          <span className="rounded-full bg-genie-primary/10 px-1.5 py-0.5 font-medium text-genie-primary">
                            Demo
                          </span>
                        )}
                        {genie.is_marketplace && (
                          <span className="rounded-full bg-genie-primary/10 px-1.5 py-0.5 font-medium text-genie-primary">
                            Marketplace
                          </span>
                        )}
                      </div>
                    </TableCell>
                    <TableCell className="hidden md:table-cell">
                      <div className="text-sm text-genie-text-primary">{genie.owner_name ?? "—"}</div>
                      {genie.owner_email && (
                        <CopyableEmail email={genie.owner_email} className="text-xs text-genie-text-secondary" />
                      )}
                    </TableCell>
                    <TableCell className="whitespace-nowrap">
                      {genie.last_conversation_at ? (
                        <>
                          <div className="text-sm text-genie-text-primary">
                            {formatDistanceToNowStrict(new Date(genie.last_conversation_at), { addSuffix: true })}
                          </div>
                          <div className="text-xs text-genie-text-secondary">{formatDate(genie.last_conversation_at)}</div>
                        </>
                      ) : (
                        <Badge variant="outline" className="text-genie-text-muted">
                          Never used
                        </Badge>
                      )}
                    </TableCell>
                    <TableCell className="hidden md:table-cell whitespace-nowrap">
                      {genie.updated_at ? (
                        <>
                          <div
                            className={`text-sm ${
                              isEditedSinceActivity(genie) ? "font-medium text-genie-primary" : "text-genie-text-primary"
                            }`}
                          >
                            {formatDistanceToNowStrict(new Date(genie.updated_at), { addSuffix: true })}
                          </div>
                          <div className="text-xs text-genie-text-secondary">{formatDate(genie.updated_at)}</div>
                        </>
                      ) : (
                        <span className="text-sm text-genie-text-secondary">—</span>
                      )}
                    </TableCell>
                    <TableCell className="hidden lg:table-cell text-sm text-genie-text-secondary">
                      {genie.conversation_count.toLocaleString()}
                    </TableCell>
                    <TableCell className="hidden lg:table-cell text-sm text-genie-text-secondary whitespace-nowrap">
                      {formatDate(genie.created_at)}
                    </TableCell>
                    <TableCell className="hidden sm:table-cell">
                      <div className="flex items-center gap-2 text-genie-text-secondary">
                        {genie.has_phone && <Phone className="h-4 w-4" aria-label="Phone number" />}
                        {genie.has_mailbox && <Mail className="h-4 w-4" aria-label="Mailbox" />}
                        {!genie.has_phone && !genie.has_mailbox && <span className="text-sm">—</span>}
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>

          {totalCount > 0 && (
            <div className="flex flex-col sm:flex-row items-center justify-between gap-4 p-4 border-t border-genie-border text-sm">
              <div className="text-genie-text-secondary order-2 sm:order-1">
                Showing {(page - 1) * INACTIVE_GENIES_PAGE_SIZE + 1} to{" "}
                {Math.min(page * INACTIVE_GENIES_PAGE_SIZE, totalCount)} of {totalCount} inactive genies
              </div>
              {totalCount > INACTIVE_GENIES_PAGE_SIZE && (
                <div className="flex gap-2 order-1 sm:order-2 w-full sm:w-auto">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setPage((p) => p - 1)}
                    disabled={page === 1}
                    className="flex-1 sm:flex-none min-h-[44px]"
                  >
                    Previous
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setPage((p) => p + 1)}
                    disabled={page * INACTIVE_GENIES_PAGE_SIZE >= totalCount}
                    className="flex-1 sm:flex-none min-h-[44px]"
                  >
                    Next
                  </Button>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      <DeleteInactiveGeniesSheet
        genies={selectedGenies}
        isOpen={isDeleteOpen}
        onClose={() => setIsDeleteOpen(false)}
        onDeleted={handleDeleted}
      />
    </div>
  );
};

export default InactiveGeniesPage;
